// Tier 3 (AI-Powered SageMaster) — UI components extracted from LessonsHub.jsx
// Emerald-themed (different from Tier 1+2 gold). Gates by Tier 2 graduation.
// Milestone gate has 6 items (1 auto-verified + 5 self-reported "30-day AI routine").
//
// Visual identity: emerald accent #10b981, glow shadow on cert, and a subtle
// scrolling chip animation on the SuPER Certified pill replacement.

import React, { useState } from "react";
import { THEME, flexxState } from "./state";
import Portal from "./Portal";
import { FlexxCertificate, openCertificatePrintView } from "./FlexxCertificate";

export const TIER3_PASS_THRESHOLD = 0.75;
export const TIER3_LESSON_XP = 50;       // first-pass XP per Tier 3 lesson
export const TIER3_QUIZ_PASS_BONUS = 8;  // +8 XP per Tier 3 quiz pass
export const TIER3_TIER_BONUS = 200;     // Tier 3 completion bonus
export const TIER3_PHASES = ["3.1", "3.2", "3.3", "3.4", "3.5"];
export const TIER3_EMERALD = "#10b981";
export const TIER3_EMERALD_GLOW = "rgba(16,185,129,0.35)";

export function Tier3Header({ passed, total, unlocked, certified }) {
  return (
    <div data-testid="tier3-header" style={{
      display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap",
      marginTop: 36, marginBottom: 14, paddingTop: 18,
      borderTop: `1px solid ${THEME.border}`,
    }}>
      <div style={{ fontSize: 11, letterSpacing: 2.5, color: TIER3_EMERALD, fontFamily: "monospace" }}>
        {unlocked ? "TIER 3 · LESSONS" : "🔒 TIER 3"}
      </div>
      <div style={{ color: THEME.text, fontSize: 22, fontWeight: 800, fontFamily: "'Bebas Neue', 'Oswald', sans-serif", letterSpacing: 0.5 }}>
        AI-POWERED — SageMaster Tools
      </div>
      {unlocked && (
        <div data-testid="tier3-progress" style={{ marginLeft: "auto", color: THEME.mute, fontFamily: "monospace", fontSize: 12 }}>
          {passed} / {total} passed
        </div>
      )}
      {certified && (
        <div data-testid="tier3-certified-pill" style={{
          marginLeft: unlocked ? 0 : "auto",
          padding: "4px 10px", border: `1px solid ${TIER3_EMERALD}`, borderRadius: 999,
          color: TIER3_EMERALD, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5,
          background: `${TIER3_EMERALD}15`,
          boxShadow: `0 0 14px ${TIER3_EMERALD_GLOW}`,
        }}>
          🤖 AI TRADER
        </div>
      )}
    </div>
  );
}

export function Tier3GatePlaceholder() {
  return (
    <div data-testid="tier3-locked-placeholder" style={{
      padding: 22, background: "#0a1612", border: `1px dashed ${TIER3_EMERALD}55`,
      borderLeft: `3px solid ${TIER3_EMERALD}`, borderRadius: 10, textAlign: "center",
    }}>
      <div style={{ fontSize: 36, marginBottom: 6 }}>🔒</div>
      <div style={{ color: TIER3_EMERALD, fontFamily: "monospace", fontSize: 11, letterSpacing: 2, marginBottom: 8 }}>TIER 3 LOCKED</div>
      <div style={{ color: THEME.text, fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: "0 auto" }}>
        Earn SuPER CERTIFIED to unlock AI Trading.
      </div>
      <div style={{ color: THEME.mute, fontSize: 12, lineHeight: 1.6, maxWidth: 480, margin: "8px auto 0", fontStyle: "italic" }}>
        25 lessons · 5 modules · 9 SageMaster AI tools · ends with the AI TRADER credential.
      </div>
    </div>
  );
}

export function Tier4PreviewLocked() {
  return (
    <div data-testid="tier4-preview-locked" style={{
      marginTop: 28, padding: 22, background: "#0d1218", border: `1px dashed ${THEME.border}`,
      borderRadius: 10, textAlign: "center", opacity: 0.85,
    }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>🔒</div>
      <div style={{ color: THEME.mute, fontFamily: "monospace", fontSize: 10, letterSpacing: 2, marginBottom: 6 }}>TIER 4 · COMING SOON</div>
      <div style={{ color: THEME.text, fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: "0 auto" }}>
        EXPERT — Multi-Asset Mastery &amp; Edge Stacking
      </div>
      <div style={{ color: THEME.mute, fontSize: 11, lineHeight: 1.6, marginTop: 8, fontFamily: "monospace" }}>
        UNLOCKS WHEN TIER 4 LESSONS ARE RELEASED
      </div>
    </div>
  );
}

// 6-item milestone gate: 1 auto + 5 self-reported "30-day AI routine".
export function Tier3MilestoneGate({ tier3Lessons, completion, onConfirm, onClose, modalBg, modalCard, primaryBtn, ghostBtn }) {
  const allLessonsDone = tier3Lessons.every((l) => completion[l.id]?.passed);
  const allQuizzesPassedAt75 = tier3Lessons.every((l) => (completion[l.id]?.score ?? 0) >= TIER3_PASS_THRESHOLD);
  const [smAccount, setSmAccount] = useState(false);
  const [screenerSetup, setScreenerSetup] = useState(false);
  const [routine30, setRoutine30] = useState(false);
  const [helixReview, setHelixReview] = useState(false);

  const items = [
    { id: "lessons", label: "25 Tier 3 lessons completed", done: allLessonsDone, manual: false },
    { id: "quizzes", label: "All module quizzes passed (75% min)", done: allQuizzesPassedAt75, manual: false },
    { id: "sm-account", label: "SageMaster account created", done: smAccount, manual: true, set: setSmAccount },
    { id: "screener", label: "AI Screener configured for your watchlist", done: screenerSetup, manual: true, set: setScreenerSetup },
    { id: "routine", label: "30-day AI routine completed (every session)", done: routine30, manual: true, set: setRoutine30 },
    { id: "helix", label: "Helix Sunday report reviewed at least once", done: helixReview, manual: true, set: setHelixReview },
  ];
  const allReady = items.every((it) => it.done);

  return (
    <Portal>
      <div data-testid="tier3-milestone-gate-modal" style={modalBg}>
        <div style={{ ...modalCard, borderColor: `${TIER3_EMERALD}55`, boxShadow: `0 0 60px ${TIER3_EMERALD_GLOW}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: "monospace", color: TIER3_EMERALD, fontSize: 10, letterSpacing: 2 }}>TIER 3 · MILESTONE GATE</div>
              <h2 style={{ margin: "6px 0 0", color: THEME.text, fontSize: 24, fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 0.5 }}>
                THE TOOLS DON'T TRADE FOR YOU
              </h2>
            </div>
            <button data-testid="tier3-gate-close" onClick={onClose} style={ghostBtn}>Close</button>
          </div>

          <div style={{ display: "grid", gap: 8, marginTop: 6, marginBottom: 16 }}>
            {items.map((it) => (
              <label key={it.id} data-testid={`tier3-gate-item-${it.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: 12, borderRadius: 8,
                  background: it.done ? `${TIER3_EMERALD}10` : "#0d1218",
                  border: `1px solid ${it.done ? TIER3_EMERALD : THEME.border}`,
                  borderLeft: `3px solid ${it.done ? TIER3_EMERALD : TIER3_EMERALD}`,
                  cursor: it.manual ? "pointer" : "default",
                }}>
                <input type="checkbox" checked={it.done}
                  disabled={!it.manual}
                  onChange={(e) => it.manual && it.set(e.target.checked)}
                  data-testid={`tier3-gate-check-${it.id}`}
                  style={{ width: 18, height: 18, accentColor: TIER3_EMERALD, cursor: it.manual ? "pointer" : "not-allowed" }} />
                <span style={{ color: it.done ? THEME.text : THEME.mute, fontSize: 13, lineHeight: 1.5, flex: 1 }}>
                  {it.label}
                  {!it.manual && it.done && <span style={{ color: TIER3_EMERALD, fontFamily: "monospace", fontSize: 10, marginLeft: 8 }}>· auto-verified</span>}
                </span>
              </label>
            ))}
          </div>

          <div style={{
            padding: 14, marginBottom: 16, borderRadius: 10,
            background: `${TIER3_EMERALD}10`, border: `1px solid ${TIER3_EMERALD}55`, borderLeft: `3px solid ${TIER3_EMERALD}`,
            color: THEME.text, fontSize: 13, lineHeight: 1.7, fontStyle: "italic",
          }}>
            <div style={{ fontFamily: "monospace", color: TIER3_EMERALD, fontSize: 9, letterSpacing: 2, marginBottom: 6, fontStyle: "normal" }}>AXEL — MILESTONE GATE</div>
            "The tools mean nothing if you don't use them. 30 days. Every day. AI Screener at session open. DeepSage before every trade. Helix every Sunday. Do that for 30 days and come back here. The AI TRADER badge is earned — not clicked."
          </div>

          <button data-testid="tier3-gate-confirm-btn" onClick={onConfirm}
            disabled={!allReady}
            style={{
              ...primaryBtn, width: "100%", padding: "14px 18px", fontSize: 13,
              background: TIER3_EMERALD, color: "#0a0a0a", borderColor: TIER3_EMERALD,
              opacity: allReady ? 1 : 0.4, cursor: allReady ? "pointer" : "not-allowed",
            }}>
            I'VE COMPLETED MY 30-DAY AI ROUTINE →
          </button>
          {!allReady && (
            <div data-testid="tier3-gate-waiting" style={{ marginTop: 10, color: THEME.mute, fontSize: 11, fontFamily: "monospace", textAlign: "center", letterSpacing: 1 }}>
              Tick all 6 items above to confirm.
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}

// Emerald-themed AI TRADER certificate.
export function Tier3CertificateModal({ onClose, modalBg, modalCard, primaryBtn, ConfettiComp }) {
  const profile = flexxState.profile() || {};
  const issued = flexxState.tier3Graduate();
  const dateStr = issued
    ? new Date(issued).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "—";
  const signedCert = (flexxState.tierCerts() || {})["3"] || null;
  const studentName = profile.name || "FLEXX Trader";
  return (
    <Portal>
      <div data-testid="tier3-certificate-modal">
        {ConfettiComp && <ConfettiComp />}
        <FlexxCertificate
          studentName={studentName}
          courseName="AI-Powered Trading — SageMaster & Helix"
          courseSubtitle="An advanced component of the FLEXX ACADEMY Elite Trading Series"
          completionDate={dateStr}
          verifyCode={signedCert?.cert_id || ""}
          tier="ai"
          onClose={onClose}
          onDownload={() => openCertificatePrintView({
            studentName, tier: "ai",
            courseName: "AI-Powered Trading — SageMaster & Helix",
            courseSubtitle: "An advanced component of the FLEXX ACADEMY Elite Trading Series",
            completionDate: dateStr, verifyCode: signedCert?.cert_id || "",
          })} />
      </div>
    </Portal>
  );
}
